import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import supabase from '../supabase/supabase';
import { FiCalendar, FiUser, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (fetchError) {
      console.error('Blog fetch error:', fetchError);
      setError(fetchError.message);
    } else if (data) {
      const publishedPosts = data.filter(p => p.published === true || p.published === 'true');

      publishedPosts.sort((a, b) => {
        const dateA = new Date(a.published_at || a.created_at);
        const dateB = new Date(b.published_at || b.created_at);
        return dateB - dateA;
      });

      setPosts(publishedPosts);
    }

    setLoading(false);
  };

  return (
    <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-28 md:pt-36 lg:pt-24">
      <Navbar />
      <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 w-full">

        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold mb-4">Our Journal</span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-brand-darkSage mb-4">The Ceylanthro Blog</h1>
          <p className="text-brand-lightText max-w-lg mx-auto text-sm sm:text-base">Stories, insights, and knowledge about authentic Ceylon wellness, sustainable sourcing, and natural living.</p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-brand-lightText font-serif italic">Loading articles...</div>
        ) : error ? (
          <div className="text-center py-20 bg-white/50 rounded-3xl border border-white">
            <p className="text-red-400 text-sm mb-4">Unable to load blog posts.</p>
            <p className="text-brand-lightText text-xs">{error}</p>
            <button
              onClick={fetchPosts}
              className="mt-4 text-sm font-semibold text-brand-gold hover:text-brand-darkSage transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white/50 rounded-3xl border border-white">
            <p className="text-brand-lightText text-lg">No articles published yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {posts.length > 0 && posts[0].cover_image && (
              <Link to={`/blog/${posts[0].slug}`} className="block mb-10 md:mb-12 group">
                <div className="bg-white/80 rounded-3xl border border-white shadow-xl overflow-hidden flex flex-col lg:flex-row">
                  <div className="relative w-full lg:w-1/2 h-64 sm:h-72 md:h-80 lg:h-auto lg:min-h-[420px] overflow-hidden flex-shrink-0">
                    <img
                      src={posts[0].cover_image}
                      alt={posts[0].title}
                      className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="w-full lg:w-1/2 p-6 sm:p-8 lg:p-10 xl:p-12 flex flex-col justify-center">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-gold mb-3">Featured</span>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif text-brand-darkSage mb-4 group-hover:text-brand-gold transition-colors">
                      {posts[0].title}
                    </h2>
                    <p className="text-brand-lightText mb-6 line-clamp-3 text-sm sm:text-base">{posts[0].excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-brand-lightText">
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiCalendar} size={12} />
                        {new Date(posts[0].published_at || posts[0].created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiUser} size={12} />
                        {posts[0].author}
                      </span>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-darkSage group-hover:text-brand-gold transition-colors">
                      Read Article <SafeIcon icon={FiArrowRight} size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Post Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {(posts.length > 0 && posts[0].cover_image ? posts.slice(1) : posts).map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                  <div className="bg-white/70 rounded-3xl border border-white shadow-sm overflow-hidden hover:shadow-xl transition-all duration-500 h-full flex flex-col">
                    {post.cover_image && (
                      <div className="h-44 sm:h-48 overflow-hidden">
                        <img
                          src={post.cover_image}
                          alt={post.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    )}
                    <div className="p-5 sm:p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs text-brand-lightText mb-3">
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiCalendar} size={11} />
                          {new Date(post.published_at || post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <span>·</span>
                        <span>{post.author}</span>
                      </div>
                      <h3 className="font-serif text-base sm:text-lg text-brand-darkSage mb-2 group-hover:text-brand-gold transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-brand-lightText line-clamp-3 flex-1">{post.excerpt}</p>
                      <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-darkSage group-hover:text-brand-gold transition-colors">
                        Read More <SafeIcon icon={FiArrowRight} size={12} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Blog;